import React from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  icon?: React.ReactNode;
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  severity?: ErrorSeverity;
  onRetry?: () => void;
  actions?: ErrorStateAction[];
  showHomeLink?: boolean;
  showBackButton?: boolean;
  details?: string;
  className?: string;
}

/**
 * ErrorState - Consistent error state component with retry actions
 *
 * Usage:
 * ```tsx
 * <ErrorState
 *   title="Failed to load trades"
 *   message="We couldn't retrieve your trading data. Please try again."
 *   onRetry={handleRetry}
 *   showHomeLink
 * />
 * ```
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  severity = 'error',
  onRetry,
  actions = [],
  showHomeLink = false,
  showBackButton = false,
  details,
  className = '',
}) => {
  const severityStyles = {
    error: {
      iconBg: 'bg-red-500/10 border-red-500/30',
      iconColor: 'text-red-400',
      accentColor: 'border-red-500/20',
    },
    warning: {
      iconBg: 'bg-amber-500/10 border-amber-500/30',
      iconColor: 'text-amber-400',
      accentColor: 'border-amber-500/20',
    },
    info: {
      iconBg: 'bg-blue-500/10 border-blue-500/30',
      iconColor: 'text-blue-400',
      accentColor: 'border-blue-500/20',
    },
  };

  const styles = severityStyles[severity];

  // Combine default and custom actions
  const allActions: ErrorStateAction[] = [];

  if (onRetry) {
    allActions.push({
      label: 'Try Again',
      onClick: onRetry,
      variant: 'primary',
      icon: <RefreshCw className="w-4 h-4 mr-2" />,
    });
  }

  if (showBackButton) {
    allActions.push({
      label: 'Go Back',
      onClick: () => window.history.back(),
      variant: 'outline',
      icon: <ArrowLeft className="w-4 h-4 mr-2" />,
    });
  }

  if (showHomeLink) {
    allActions.push({
      label: 'Go Home',
      href: '/app',
      variant: 'ghost',
      icon: <Home className="w-4 h-4 mr-2" />,
    });
  }

  allActions.push(...actions);

  return (
    <Card
      hoverEffect={false}
      className={`p-8 text-center border-l-4 ${styles.accentColor} ${className}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {/* Icon */}
      <div
        className={`w-16 h-16 mx-auto mb-4 rounded-full border flex items-center justify-center ${styles.iconBg} transition-all duration-300`}
        aria-hidden="true"
      >
        <AlertTriangle className={`w-8 h-8 ${styles.iconColor}`} />
      </div>

      {/* Title */}
      <h3 className="text-lg font-bold text-white mb-2" id="error-title">{title}</h3>

      {/* Message */}
      <p className="text-slate-400 mb-6 max-w-md mx-auto" id="error-message">{message}</p>

      {/* Details (for dev mode or verbose errors) */}
      {details && import.meta.env.DEV && (
        <pre
          className="mb-6 text-left text-xs text-slate-300 bg-black/20 rounded-md p-4 overflow-auto max-h-32 max-w-lg mx-auto"
          aria-label="Error details"
        >
          {details}
        </pre>
      )}

      {/* Actions */}
      {allActions.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3" role="group" aria-label="Error recovery actions">
          {allActions.map((action, index) => {
            if (action.href) {
              return (
                <a
                  key={index}
                  href={action.href}
                  className={getButtonClasses(action.variant || 'primary')}
                >
                  {action.icon && <span aria-hidden="true">{action.icon}</span>}
                  {action.label}
                </a>
              );
            }
            return (
              <Button
                key={index}
                type="button"
                variant={action.variant || 'primary'}
                onClick={action.onClick}
              >
                {action.icon && <span aria-hidden="true">{action.icon}</span>}
                {action.label}
              </Button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/**
 * Inline error message component for form fields and smaller contexts
 */
export interface InlineErrorProps {
  message: string;
  className?: string;
  id?: string;
}

export const InlineError: React.FC<InlineErrorProps> = ({ message, className = '', id }) => {
  return (
    <div
      id={id}
      role="alert"
      aria-live="polite"
      className={`flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 ${className}`}
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
};

/**
 * Helper to get button-like classes for anchor elements
 * Includes proper focus states for accessibility
 */
function getButtonClasses(variant: string): string {
  const base = [
    'inline-flex items-center justify-center rounded-md font-bold',
    'transition-all duration-300 tracking-wide px-6 py-3 text-base',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-st-dark',
  ].join(' ');

  const variants: Record<string, string> = {
    primary: 'bg-st-primary text-white hover:bg-[#12C4BE] shadow-lg shadow-st-primary/20 focus-visible:ring-st-primary',
    secondary: 'bg-slate-700 text-white hover:bg-slate-600 focus-visible:ring-slate-500',
    outline: 'border-2 border-st-primary text-st-primary hover:bg-st-primary hover:text-white focus-visible:ring-st-primary',
    ghost: 'text-st-muted hover:text-st-primary focus-visible:ring-st-primary',
  };

  return `${base} ${variants[variant] || variants.primary}`;
}

export default ErrorState;
